import { useCallback,useEffect,useRef,useState,useSyncExternalStore } from 'react';
import { scopeAffectsRead } from '../../api/dataScopes';
import { DataDomain,domainsForCacheKey } from '../../api/dataDomains';
import { DataChange,subscribeDataChanges } from '../../api/dataSync';
import { queryRevision,peekQuery,peekQueryState,putQuery,readQuery,readSessionKey } from '../../api/queryCache';
import { useCriticalRead } from './readiness';
import { useRetainedRoute,useRouteActive } from './runtime';

/** Lifecycle for visited tabs and server-side filters; speculative reads use readiness.ts. */
export type ReadPolicy = { staleTime: number; revalidateInterval: number };
export const RETAINED_READ_POLICY: ReadPolicy = { staleTime: 30_000, revalidateInterval: 5_000 };
export const isReadFresh = (updatedAt: number, policy: ReadPolicy = RETAINED_READ_POLICY) => updatedAt > 0 && Date.now() - updatedAt < policy.staleTime;

const subscribeVisibility = (notify: () => void) => {
    document.addEventListener('visibilitychange', notify);
    return () => document.removeEventListener('visibilitychange', notify);
};
/** Browser visibility is independent from the app's retained-route activity. */
export function useDocumentVisible() {
    return useSyncExternalStore(subscribeVisibility, () => document.visibilityState !== 'hidden', () => true);
}

/** Keep navigation metadata through query transitions, never data or write permissions.
 * Undefined means pending; a confirmed empty value replaces the prior snapshot.
 * Scope includes the business object/range but excludes search, sort and active tab.
 */
export function useNavigationSnapshot<T>(scope:string, value:T|undefined):T|undefined {
    const identity=`${readSessionKey()}|${scope}`;
    const previous=useRef<{identity:string;value:T|undefined}>({identity,value});
    if(previous.current.identity!==identity)previous.current={identity,value};
    else if(value!==undefined)previous.current.value=value;
    return value===undefined?previous.current.value:value;
}

/** Latest callback without subscription churn or remounting editable children. */
export function useDataRefresh(domains: readonly DataDomain[], refresh: (change: DataChange) => unknown, enabled = true, ignoreInactiveFocus = false, readKey?:string, coalesce=false) {
    const active = useRouteActive();
    const retained = useRetainedRoute();
    const documentVisible=useDocumentVisible();
    enabled = enabled && active && documentVisible;
    const latest = useRef(refresh); latest.current = refresh;
    const enabledRef = useRef(enabled); enabledRef.current = enabled;
    const deferred = useRef<DataChange | null>(null);
    const session=readSessionKey();
    const subscription=JSON.stringify([session,readKey]);
    const subscribedKey=useRef(subscription);
    if(subscribedKey.current!==subscription){subscribedKey.current=subscription;deferred.current=null;}
    useEffect(() => {
        if (!enabled || !deferred.current) return;
        let disposed=false;
        void Promise.resolve().then(() => {
            if (disposed || !enabledRef.current || subscribedKey.current!==subscription || readSessionKey()!==session) return; // A rapid second switch must not consume the invalidation.
            const change = deferred.current; deferred.current = null;
            if (change) return latest.current(change);
        }).catch(error => console.error('Data refresh failed', error));
        return ()=>{disposed=true;};
    }, [enabled,subscription,session]);
    const key = domains.join('|');
    useEffect(() => {
        let disposed=false;
        let running=false;
        let queued:DataChange|null=null;
        const merge=(previous:DataChange|null,next:DataChange):DataChange=>previous ? {
            domains:Array.from(new Set([...previous.domains,...next.domains])),url:'',method:'GET',reason:'manual',
        } : next;
        const dispatch=(change:DataChange)=>{
            if(disposed || readSessionKey()!==session)return;
            if(!enabledRef.current){
                if(ignoreInactiveFocus && change.reason==='focus')return;
                deferred.current=merge(deferred.current,change);
                return;
            }
            if(running && coalesce){queued=merge(queued,change);return;}
            try {
                const result=latest.current(change);
                if(result && typeof (result as Promise<unknown>).then==='function'){
                    running=true;
                    void Promise.resolve(result).catch(error=>console.error('Data refresh failed',error)).finally(()=>{
                        running=false;
                        if(queued && !disposed){const next=queued;queued=null;dispatch(next);}
                    });
                }
            }catch(error){console.error('Data refresh failed',error);}
        };
        const stop=subscribeDataChanges(change=>{
            if(change.domains.some(domain=>key.split('|').includes(domain)) && (!readKey || scopeAffectsRead(readKey,change.scope)))dispatch(change);
        });
        return ()=>{disposed=true;stop();};
    }, [key, retained, ignoreInactiveFocus, readKey, coalesce,session]);
    return deferred;
}
/** For existing cancellable effects; add the revision to the read's dependencies. */
export function useDataRevision(domains: readonly DataDomain[]) {
    const [revision, setRevision] = useState(0);
    useDataRefresh(domains, () => setRevision(value => value + 1));
    return revision;
}

// All ongoing jobs/stock watchers share a scheduler; invisible tabs do not poll.
type Poll = { run: () => unknown; interval: number; due: number; pending: boolean };
const polls = new Set<Poll>();
let pollTimer: ReturnType<typeof setTimeout> | undefined;
function schedulePolls() {
    if (pollTimer || !polls.size) return;
    const delay = Math.max(100, Math.min(...Array.from(polls, poll => poll.due - Date.now())));
    pollTimer = setTimeout(() => {
        pollTimer = undefined;
        for (const poll of polls) {
            if (poll.due > Date.now()) continue;
            poll.due = Date.now() + poll.interval;
            if (poll.pending || document.visibilityState === 'hidden') continue;
            poll.pending = true;
            void Promise.resolve().then(poll.run).catch(() => {}).finally(() => { poll.pending = false; });
        }
        schedulePolls();
    }, delay);
}
export function useDataPolling(run: () => unknown, interval: number, enabled: boolean) {
    const active = useRouteActive();
    enabled = enabled && active;
    const latest = useRef(run); latest.current = run;
    useEffect(() => {
        if (!enabled) return;
        const poll: Poll = { run: () => latest.current(), interval, due: Date.now() + interval, pending: false };
        polls.add(poll); schedulePolls();
        return () => {
            polls.delete(poll);
            if (!polls.size && pollTimer) { clearTimeout(pollTimer); pollTimer = undefined; }
        };
    }, [interval, enabled]);
}

/** Visibility gates the read. Closing cancels it; mutation invalidation is shared with all API reads. */
export const resourcePrefetch = <T,>(key:string, read:(signal:AbortSignal)=>Promise<T>) => ({
    key:`view|${key}`,read:(signal:AbortSignal)=>readQuery(`view|${key}`,read,{signal,staleTime:30_000}),
});

export function useOnDemandResource<T>(key: string, enabled: boolean,
    read: (signal: AbortSignal) => Promise<T>, refreshOnChange = true,
    reconcile?: (current: T | undefined, change: DataChange) => T | undefined, policy?: ReadPolicy) {
    const active = useRouteActive();
    const session = readSessionKey();
    const identity = JSON.stringify([session, key]);
    const identityRef = useRef(identity); identityRef.current = identity;
    const mounted=useRef(true);
    useEffect(()=>{mounted.current=true;return()=>{mounted.current=false;};},[]);
    const isCurrent = useCallback(() => mounted.current && identityRef.current === identity && readSessionKey() === session, [identity, session]);
    const snapshot = peekQueryState<T>(`view|${key}`, policy?.staleTime ?? 30_000);
    const loadedAt = useRef(snapshot?.updatedAt ?? 0);
    const documentVisible = useDocumentVisible();
    enabled = enabled && active && documentVisible;
    const cacheKey = `view|${key}`;
    const [state, setState] = useState<{ key: string; data?: T; loading: boolean; error: boolean }>(
        () => ({ key: identity, data: snapshot?.value, loading: enabled && !snapshot, error: false }));
    const current = useRef(state); current.current = state;
    const latest = useRef(read); latest.current = read;
    const pending = useRef<AbortController | null>(null);
    const flight = useRef<{key:string; controller:AbortController; promise:Promise<void>} | null>(null);
    const load = useCallback((allowCached = false):Promise<void> => {
        if (!enabled || !isCurrent()) return Promise.resolve();
        const existing=flight.current;
        if(existing?.key===identity && !existing.controller.signal.aborted)return existing.promise;
        pending.current?.abort();
        const controller=new AbortController(); pending.current=controller;
        const task={key:identity,controller,promise:Promise.resolve()};
        // Schedule after registering the flight so every same-tick caller joins it.
        task.promise=Promise.resolve().then(async()=>{
            if (controller.signal.aborted || !isCurrent()) return;
            setState(previous=>({key:identity,data:previous.key===identity?previous.data:undefined,
                loading:previous.key!==identity || previous.data===undefined,error:false}));
            while(!controller.signal.aborted){
                const revision=queryRevision(cacheKey);
                try{
                    const data=await readQuery(cacheKey,signal=>latest.current(signal),{signal:controller.signal,staleTime:allowCached ? policy?.staleTime ?? 30_000 : 0});
                    if(controller.signal.aborted || !isCurrent())return;
                    if(queryRevision(cacheKey)!==revision)continue;
                    loadedAt.current=Date.now();
                    const next={key:identity,data,loading:false,error:false};
                    current.current=next;putQuery(cacheKey,data);setState(next);return;
                }catch(error){
                    if(controller.signal.aborted || !isCurrent())return;
                    // Only a newer invalidation justifies another read. Never retry a failed request.
                    if(queryRevision(cacheKey)!==revision)continue;
                    if((error as {name?:string})?.name!=='AbortError')setState(previous=>({...previous,loading:false,error:true}));
                    return;
                }
            }
        }).finally(()=>{
            if(pending.current===controller)pending.current=null;
            if(flight.current===task)flight.current=null;
        });
        flight.current=task;return task.promise;
    }, [enabled, identity, cacheKey, isCurrent, policy?.staleTime]);
    const reload = useCallback(() => load(), [load]);
    useEffect(() => {
        if (enabled && !deferredChange.current) {
            const cached = peekQueryState<T>(cacheKey, policy?.staleTime ?? 30_000);
            if (cached) {
                loadedAt.current = cached.updatedAt;
                if (current.current.key !== identity || current.current.data === undefined)
                    setState({ key: identity, data: cached.value, loading: false, error: false });
            } else void load(true);
        }
        return () => {
            if (pending.current && !pending.current.signal.aborted) {
                pending.current.abort();
                if (policy) loadedAt.current = 0;
            }
        };
    }, [enabled, load, identity, policy, cacheKey]);
    const deferredChange = useDataRefresh(domainsForCacheKey(key), change => {
        if (!isCurrent()) return;
        if (change.reason === 'focus' && ((pending.current && !pending.current.signal.aborted) || isReadFresh(loadedAt.current, policy))) return;
        // A complete, server-confirmed projection replaces a redundant GET.
        // Undefined means this event cannot fully reconcile this resource.
        const confirmed = reconcile?.(current.current.key === identity ? current.current.data : undefined, change);
        if (confirmed !== undefined) {
            pending.current?.abort(); pending.current = null;
            const next = { key: identity, data: confirmed, loading: false, error: false };
            loadedAt.current = Date.now();
            current.current = next; putQuery(cacheKey, confirmed); setState(next);
            return;
        }
        return reload();
    }, enabled && refreshOnChange, true, cacheKey);
    useDataPolling(() => {
        // Failed reads wait for an explicit refresh, relevant data change or reactivation.
        if (policy && !current.current.error && !pending.current && !isReadFresh(loadedAt.current, policy)) return reload();
    }, policy?.revalidateInterval ?? 30_000, !!policy && enabled && refreshOnChange);
    const data = state.key===identity && state.data!==undefined ? state.data : peekQuery<T>(cacheKey, policy?.staleTime ?? 30_000);
    const error = state.key===identity && state.error;
    const initialPending = enabled && data===undefined && !error;
    useCriticalRead(initialPending);
    const setData=useCallback((data:T)=>{if(!isCurrent())return;pending.current?.abort();loadedAt.current=Date.now();putQuery(cacheKey,data);const next={key:identity,data,loading:false,error:false};current.current=next;setState(next);},[identity,cacheKey,isCurrent]);
    return { data, loading: initialPending || (enabled && state.key===identity && state.loading),
        error: state.key === identity && state.error, reload,
        setData };
}


/** Shared option reads: lazy open, immediate first page, short search coalescing,
 * bounded per-open cache and cancellation. Never cache eligibility across openings. */
export function useOptionQuery<T>({load,enabled,query='',delay=120,minLength=0}:{
    load?: (query:string,signal:AbortSignal)=>Promise<T[]>;enabled:boolean;query?:string;delay?:number;minLength?:number;
}) {
    const active=useRouteActive();
    enabled=enabled && active && !!load;
    const key=query.trim().replace(/\s+/g,' ');
    const [attempt,setAttempt]=useState(0);
    const [result,setResult]=useState<{key:string;load:typeof load;data:T[];error:boolean}|null>(null);
    const cache=useRef(new Map<string,T[]>());
    const settled=result?.key===key && result.load===load ? result : null;
    const loading=enabled && key.length>=minLength && !settled;
    useCriticalRead(loading);
    useEffect(()=>{cache.current.clear();setResult(null);},[enabled,load,attempt]);
    useEffect(()=>{
        if(!enabled || !load || key.length<minLength)return;
        const controller=new AbortController();
        const cached=cache.current.get(key);
        if(cached){setResult({key,load,data:cached,error:false});return;}
        setResult(null);
        const timer=setTimeout(()=>{
            Promise.resolve().then(()=>controller.signal.aborted ? [] : load(key,controller.signal)).then(data=>{
                if(controller.signal.aborted)return;
                if(cache.current.size>=20)cache.current.delete(cache.current.keys().next().value!);
                cache.current.set(key,data);setResult({key,load,data,error:false});
            }).catch(()=>{if(!controller.signal.aborted)setResult({key,load,data:[],error:true});});
        },key?Math.min(Math.max(delay,0),200):0);
        return ()=>{clearTimeout(timer);controller.abort();};
    },[enabled,load,key,minLength,delay,attempt]);
    return {data:enabled?settled?.data??[]:[],loading,error:enabled && !!settled?.error,retry:()=>setAttempt(value=>value+1)};
}
